/**
 * Webhook Bridge Mycelium Connector
 *
 * Bridges webhook delivery through PGMQ events instead of direct HTTP calls.
 * When activity events arrive, instead of immediately making HTTP requests to
 * external webhook endpoints, this connector publishes a "webhook_delivery"
 * event to PGMQ. The mycelium-worker Edge Function picks these up and
 * executes the actual HTTP delivery asynchronously.
 *
 * Benefits:
 * - Eliminates blocking HTTP calls from the client-side event path
 * - Webhook delivery becomes transactional (event + delivery in same DB)
 * - Automatic retry via PGMQ visibility timeout
 * - Dead letter queue for permanently failed deliveries
 * - Full audit trail via mycelium_event_archive
 *
 * @module mycelium-webhook-bridge
 * @since 2026-03-23
 * @see docs/protocols/mycelium/WEBHOOK_ELIMINATION_PLAYBOOK.md
 */

import {
  myceliumEventBackbone,
  type MyceliumEvent,
} from "./mycelium-event-backbone.service";
import { enqueueWebhookDelivery } from "./mycelium-webhook-enqueue";
import { logger } from "../lib/logger";

// Re-export type for backwards compatibility
export type { WebhookDeliveryEvent } from "./mycelium-webhook-enqueue";

// ============================================================================
// TYPES
// ============================================================================

/** Domains whose events can trigger webhook deliveries */
const WEBHOOK_ELIGIBLE_DOMAINS = [
  "task",
  "sales",
  "calendar",
  "accounting",
  "notification",
  "gamification",
  "crm",
  "documents",
] as const;

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class WebhookBridgeConnector {
  private initialized = false;

  /**
   * Initialize connector and subscribe to activity events.
   * Listens to all webhook-eligible domains and publishes
   * webhook_delivery events to PGMQ for async processing.
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("WebhookBridgeConnector already initialized");
      return;
    }

    logger.info("🌉 Webhook Bridge connector initializing");

    myceliumEventBackbone.subscribe({
      name: "WebhookBridgeConnector",
      domains: [...WEBHOOK_ELIGIBLE_DOMAINS],
      handler: this.handleEvent.bind(this),
      priority: 2, // Low priority — webhooks are fire-and-forget
    });

    this.initialized = true;
    logger.info(
      "✅ Webhook Bridge connector ready — webhook deliveries route through PGMQ"
    );
  }

  /**
   * Handle an activity event by publishing a webhook_delivery event to PGMQ.
   * The mycelium-worker Edge Function picks these up and delivers webhooks.
   */
  private async handleEvent(event: MyceliumEvent): Promise<void> {
    // Only bridge events that have an orgId — webhooks are org-scoped
    if (!event.orgId) {
      logger.debug("WebhookBridge: skipping event without orgId", {
        eventId: event.id,
        type: event.type,
      });
      return;
    }

    await enqueueWebhookDelivery({
      sourceDomain: event.domain,
      sourceType: event.type,
      sourceEventId: event.id,
      orgId: event.orgId,
      userId: event.userId ?? undefined,
      // userId and teamId are included for webhook consumers that need actor context.
      // Webhooks are org-scoped (requires org admin to create), so this data stays
      // within the organization's trust boundary.
      eventPayload: {
        ...event.payload,
        userId: event.userId,
        teamId: event.teamId,
      },
      originalTimestamp: new Date(event.timestamp).toISOString(),
      source: "WebhookBridgeConnector",
    });
  }

  /**
   * Dispose the connector.
   */
  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info("🧹 Webhook Bridge connector disposed");
  }
}

// ============================================================================
// SINGLETON
// ============================================================================

export const webhookBridgeConnector = new WebhookBridgeConnector();
